import {useState, useEffect, useRef} from 'react';
import { IoMdCheckmark, IoMdClipboard, IoMdCloseCircleOutline, IoMdOpen } from 'react-icons/io';

import {
  Button,
  Checkbox,
  DateInput,
  RenderIf,
  SearchInput,
  TextInput
} from "../../../../../components";
import { useDebounce } from '../../../../../hooks/useDebounce';
import { producaov2 } from '../../../../../services/apis';
import { promiseHandler } from '../../../../../utils/promises';
import { useSearchResults } from "../hooks";
import { useCertificate } from "../providers";
import { HorizontaSeparator, VerticalSeparator } from '../styles';
import { ErrorContainer, ErrorText, ErrorTitle, Label, SecondaryButton, SignatureChip, SignatureChips, SignatureEmptyMessage, SpecialistCountersContainer, SubmitActionsContainer } from './styles';
import NewTextEditor from '../../../../../components/Admin/NewTextEditor';
import SignaturesListModal from '../components/SignaturesListModal';
import { FileInput } from '../../../../../components/Inputs/FileInput';
import { ColorInput } from '../../../../../components/Inputs/ColorInput';

function SearchField({
  label = '',
  placeholder = '',
  request = (search = '') => {},
  resultExtractor = (info) => {},
  onResultSelected = (result) => {},
  defaultValue = '',
  autoFocus = false,
}) {
  const {value, onChangeValue, isLoading, results} = useSearchResults({
    request,
    resultExtractor,
    defaultValue
  });

  const onChangeResult = (result) => {
    onResultSelected(result);
  }
  
  return (
    <SearchInput
      label={label}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      onChangeText={onChangeValue}
      isLoading={isLoading}
      results={results}
      onResultPress={onChangeResult}
      onClearSelectedResultPress={() => onChangeResult(null)}
      autoFocus={autoFocus}
    />
  )
}

export function UserField ({manualUserId}) {
  const {updateOptions} = useCertificate();

  const userRequest = (search) => producaov2.get('alunos', {
    params: {
      search,
      invertOrdering: true
    }
  });

  const userExtractor = user => ({
    id: user?.id,
    text: `${user?.id} - ${user?.nome}`
  });

  const onUserSelected = (user) => {
    updateOptions({optionName: 'userId', optionValue: user?.id});
  }

  return (
    <SearchField
      label="Aluno"
      placeholder="Digite o id, email ou nome do aluno"
      defaultValue={manualUserId}
      request={userRequest}
      resultExtractor={userExtractor}
      onResultSelected={onUserSelected}
      autoFocus={!!manualUserId}
    />
  );
}

export function CourseField ({label = 'Curso', manualCourseId}) {
  const {updateOptions} = useCertificate();

  const courseRequest = (search) => producaov2.get('cursos-simplelist-new', {
    params: {
      search,
      limit: 15
    }
  });

  const courseExtractor = course => ({
    id: course?.id,
    text: `${course?.id} - ${course?.curso}`
  });

  const onCourseSelected = (course) => {
    updateOptions({optionName: 'courseId', optionValue: course?.id});
  }

  return (
    <SearchField
      label={label}
      defaultValue={manualCourseId}
      placeholder="Digite o id ou nome do curso"
      request={courseRequest}
      resultExtractor={courseExtractor}
      onResultSelected={onCourseSelected}
    />
  );
}

export function ManualCourseNameField ({label = 'Nome do Curso Personalizado'}) {
  const {updateOptions} = useCertificate();
  const [courseName, setCourseName] = useState('');
  const debouncedCourseName = useDebounce(courseName);

  useEffect(() => {
    updateOptions({optionName: 'courseName', optionValue: courseName});
  }, [debouncedCourseName])

  return (
    <TextInput
      label={label}
      placeholder="Digite o nome do curso ou deixe vazio para pegar o do curso selecionado"
      onChangeText={setCourseName}
      value={courseName}
    />
  );
}

export function CompanyLogoField ({label = 'Logo da empresa (Opcional)'}) {
  const {updateOptions} = useCertificate();

  return (
    <FileInput
      label={label}
      onChangeFile={file => updateOptions({optionName: 'companyLogoFile', optionValue: file})}
    />
  );
}

export function CompanyColorField ({label = 'Cor da empresa (Opcional)'}) {
  const {options, updateOptions} = useCertificate();

  return (
    <ColorInput
      label={label}
      value={options?.companyColor}
      onChangeColor={color => updateOptions({optionName: 'companyColor', optionValue: color})}
    />
  );
}

export function TrainingField () {
  const {updateOptions} = useCertificate();

  const courseRequest = (search) => producaov2.get('/admin/treinamentos', {
    params: {
      nome: search,
      search_id_curso: true,
      limit: 15
    }
  });

  const courseExtractor = course => ({
    id: course?.id,
    text: `${course?.id} - ${course?.nome}`
  });

  const onCourseSelected = (course) => {
    updateOptions({optionName: 'trainingId', optionValue: course?.id});
  }

  return (
    <SearchField
      label="Treinamento"
      placeholder="Digite o id ou nome do treinamento"
      request={courseRequest}
      resultExtractor={courseExtractor}
      onResultSelected={onCourseSelected}
    />
  );
}

export function InternalProjectField ({
  fieldIndex = 0
}) {
  const {updateOptions, options} = useCertificate();

  const knownInternalProjects = [
    {
      id: 3467,
      name: 'Projeto Prático Aplicado - Área Industrial'
    },
    {
      id: 3462,
      name: 'Projeto Prático Aplicado - Área de Serviços'
    },
  ];

  const filterInternalProjects = (search) => {
    const internalProjects = knownInternalProjects?.filter(project => {
      if(project?.name?.includes(search)) {
        return true;
      } else if(`${project?.id}`?.includes(search)) {
        return true;
      }

      return false;
    });

    return internalProjects;
  }

  const courseRequest = (search) => {
    return new Promise((resolve, reject) => {
      const internalProjects = filterInternalProjects(search);

      const response = {
        data: internalProjects
      }
      resolve(response);
    });
  };

  const courseExtractor = course => ({
    id: course?.id,
    text: `${course?.id} - ${course?.name}`
  });

  const onCourseSelected = (course) => {
    let lastInternalProjectsId = options?.internalProjectIds;

    if(lastInternalProjectsId?.legth <= fieldIndex) {
      lastInternalProjectsId.push(course?.id);
    } else {
      lastInternalProjectsId[fieldIndex] = course?.id;
    }

    updateOptions({optionName: 'internalProjectIds', optionValue: lastInternalProjectsId});
  }

  useEffect(() => {
    return () => {
      const internalProjects = options?.internalProjectIds;

      const newInternalProjects = internalProjects?.filter((projectId, index) => (index !== fieldIndex));

      updateOptions({optionName: 'internalProjectIds', optionValue: newInternalProjects});
    }
  }, []);

  return (
    <SearchField
      label="Projeto Pratico"
      placeholder="Digite o id ou nome do projeto"
      request={courseRequest}
      resultExtractor={courseExtractor}
      onResultSelected={onCourseSelected}
    />
  );
}

export function StarterDateField() {
  const {options, updateOptions} = useCertificate();

  const onChangeDate = (date) => {
    updateOptions({optionName: 'manualStarterDate', optionValue: date});
  }

  return (
    <TextInput
      label="Data de início (Opcional)"
      text={options?.manualStarterDate}
      onChangeText={onChangeDate}
      type="date"
    />
  )
}

export function EmissionDateField() {
  const {options, updateOptions} = useCertificate();

  const onChangeDate = (date) => {
    updateOptions({optionName: 'manualConclusionDate', optionValue: date});
  }

  return (
    <TextInput
      label="Data de emissão (Opcional)"
      text={options?.manualConclusionDate}
      onChangeText={onChangeDate}
      type="date"
    />
  )
}

export function EventDaysField() {
  const {updateOptions} = useCertificate();

  const [eventDays, setEventDays] = useState('');
  const debouncedEventDays = useDebounce(eventDays);

  useEffect(() => {
    updateOptions({optionName: 'eventCourseDays', optionValue: debouncedEventDays});
  }, [debouncedEventDays]);

  const onChangeEventDays = (text) => {
    setEventDays(text);
  }

  return (
    <TextInput
      label="Dias evento"
      placeholder="Ex: nos dias 21, 22 e 23 de Julho"
      onChangeText={onChangeEventDays}
      text={eventDays}
    />
  );
}

export function TrainingDaysField() {
  const {updateOptions} = useCertificate();

  const [eventDays, setEventDays] = useState('');
  const debouncedEventDays = useDebounce(eventDays);

  useEffect(() => {
    updateOptions({optionName: 'trainingCourseDays', optionValue: debouncedEventDays});
  }, [debouncedEventDays]);

  const onChangeEventDays = (text) => {
    setEventDays(text);
  }

  return (
    <TextInput
      label="Dias treinamento"
      placeholder="Ex: nos dias 21, 22 e 23 de Julho"
      onChangeText={onChangeEventDays}
      text={eventDays}
    />
  );
}

export function WorkloadField() {
  const {updateOptions} = useCertificate();

  const [workload, setWorkload] = useState('');
  const debouncedWorkload = useDebounce(workload);

  useEffect(() => {
    updateOptions({optionName: 'workloadHours', optionValue: debouncedWorkload});
  }, [debouncedWorkload]);

  const onChangeWorkload = (text) => {
    setWorkload(text);
  }

  return (
    <TextInput
      label="Carga horária (horas)"
      placeholder="Ex: 200"
      onChangeText={onChangeWorkload}
      text={workload}
    />
  );
}

export function ManualEmentaField() {
  const ementaRef = useRef('');

  const {updateOptions} = useCertificate();

  const [manualEmenta, setManualEmenta] = useState('');
  const debouncedManualEmenta = useDebounce(manualEmenta);

  useEffect(() => {
    updateOptions({optionName: 'manualEmenta', optionValue: debouncedManualEmenta});
  }, [debouncedManualEmenta]);

  const onChangeManualEmenta = (text) => {
    setManualEmenta(text);
  }

  return (
    <>
      <Label>Ementa</Label>
      <NewTextEditor
        haveFullscreen
        default={''}
        text={ementaRef}
        onChangeText={onChangeManualEmenta}
      />
    </>
  )
}

export function SignaturesField() {
  const {updateOptions} = useCertificate();

  const [signatures, setSignatures] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onCloseModal = () => {
    setIsOpenModal(false);
  }

  const onSubmitModal = (signatures) => {
    setSignatures(signatures);
    updateOptions({optionName: 'manualSignaturesId', optionValue: signatures?.map(signature => signature?.id)});
    onCloseModal();
  }

  const onClearSignatures = () => {
    setSignatures([]);
    updateOptions({optionName: 'manualSignaturesId', optionValue: []});
  }

  return (
    <>
      <Label>Assinaturas</Label>
      <SignatureChips onClick={() => setIsOpenModal(true)}>
        {signatures?.length === 0 && <SignatureEmptyMessage>Nenhuma assinatura selecionada</SignatureEmptyMessage>}
        {signatures?.map(signature => <SignatureChip>{`${signature?.id} - ${signature?.title}`}</SignatureChip>)}
      </SignatureChips>
      {signatures?.length > 0 && <SecondaryButton onClick={onClearSignatures}>Remover assinaturas</SecondaryButton>}
      {isOpenModal && <SignaturesListModal initialSelectedSignatures={signatures} onClose={onCloseModal} onSubmit={onSubmitModal} />}
    </>
  );
}

export function ExternalProjectField({
  fieldIndex = 0
}) {
  const {updateOptions, options} = useCertificate();

  const [projectName, setProjectName] = useState('');
  const debouncedProjectName = useDebounce(projectName);

  useEffect(() => {
    let lastProjectsName = options?.externalProjectsName;

    if(debouncedProjectName?.length === 0 && lastProjectsName?.length === 0) {
      return;
    }

    if(lastProjectsName?.length === 0 && lastProjectsName?.length <= fieldIndex) {
      lastProjectsName.push(debouncedProjectName);
    } else {
      lastProjectsName[fieldIndex] = debouncedProjectName;
    }

    updateOptions({optionName: 'externalProjectsName', optionValue: lastProjectsName});
  }, [debouncedProjectName]);

  useEffect(() => {
    return () => {
      const externalProjects = options?.externalProjectsName;
    
      const newExternalProjects = externalProjects?.filter((project, index) => (index !== fieldIndex));

      updateOptions({optionName: 'externalProjectsName', optionValue: newExternalProjects});
    }
  }, []);

  return (
    <TextInput
      label="Projeto Externo"
      placeholder="Nome do projeto externo"
      onChangeText={setProjectName}
      text={projectName}
    />
  );
}

export function SpecialistLeanSixSigmaCourseFields() {
  const [projectsCount, setProjectsCount] = useState({
    internal: 0,
    external: 0
  });

  const renderInternalCourses = () => {
    const coursesList = Array(+projectsCount.internal).fill(0);

    return coursesList.map((_, index) => {
      return (
        <>
          <VerticalSeparator />
          <InternalProjectField fieldIndex={index} />
        </>
      );
    })
  }

  const renderExternalCourses = () => {
    const coursesList = Array(+projectsCount.external).fill(0);

    return coursesList.map((_, index) => {
      return (
        <>
          <VerticalSeparator />
          <ExternalProjectField fieldIndex={index}/>
        </>
      );
    })
  }

  const onIncreaseInternalCourses = (count) => {
    setProjectsCount(prev => ({...prev, internal: count}));
  }

  const onIncreaseExternalCourses = (count) => {
    setProjectsCount(prev => ({...prev, external: count}));
  }

  return (
    <>
      <SpecialistCountersContainer>
        <TextInput
          label="Número Projeto Práticos"
          type="number"
          max={2}
          min={0}
          text={projectsCount.internal}
          onChangeText={onIncreaseInternalCourses}
          defaultValue={0}
        />
        <HorizontaSeparator />
        <TextInput
          label="Número Projetos Externos"
          type="number"
          max={3}
          min={0}
          text={projectsCount.external}
          onChangeText={onIncreaseExternalCourses}
          defaultValue={0}
        />
      </SpecialistCountersContainer>
      {renderInternalCourses()}
      {renderExternalCourses()}
    </>
  )
}

function CheckboxField({
  optionName = '',
  text = '',
  helpText = ''
}) {
  const {options, updateOptions} = useCertificate();

  const toggleSelection = () => {
    updateOptions({optionName, optionValue: !options[optionName]});
  }
  
  return (
    <Checkbox
      isSelected={!!options[optionName]}
      onSelectionPress={toggleSelection}
      text={text}
      helpText={helpText}
    />
  )
}

export function IsEnglishCheckboxField() {
  return (
    <CheckboxField
      optionName="isEnglish"
      text="Gerar em inglês?"
      helpText="Opção para gerar o certificado em inglês"
    />
  )
}

export function RegenerateCheckboxField() {
  return (
    <CheckboxField
      optionName="needRegenerate"
      text="Gerar novamente?"
      helpText="Opção para gerar um novo certificado e sobrescrever caso já tenha um gerado"  
    />
  );
}

export function HideConclusionDateCheckboxField() {
  return (
    <CheckboxField
      optionName="hideConclusionDate"
      text="Ocultar data de conclusão"
      helpText="Opção para gerar o certificado sem a data de conclusão"  
    />
  );
}

export function OnlyEmentaCheckboxField() {
  return (
    <CheckboxField
      optionName="onlyEmenta"
      text="Somente ementa?"
      helpText="Opção para gerar somente a ementa do certificado"  
    />
  );
}

export function IncludeEmentaCheckboxField() {
  return (
    <CheckboxField
      optionName="includeEmenta"
      text="Incluir ementa?"
      helpText="Opção para gerar o certificado com a ementa"  
    />
  );
}

export function GenerateEspecialistCheckboxField() {
  return (
    <CheckboxField
      optionName="withSpecialistCopy"
      text="Gerar com copy de especialista?"
      helpText="Opção para gerar o certificado com o copy de Especialista"  
    />
  );
}

export function IncludeCourseSignaturesCheckboxField() {
  return (
    <CheckboxField
      optionName="includeCourseSignatures"
      text="Incluir assinatura dos consultores do curso?"
      helpText="Opção para incluir as assinaturas dos consultores do curso selecionado"  
    />
  );
}

export function NeedSendEmailCheckboxField() {
  return (
    <CheckboxField
      optionName="needSendEmail"
      text="Enviar certificado por email?"
      helpText="Opção para enviar o certificado para o email do aluno"  
    />
  );
}

export function NeedSendEmailToManagerCheckboxField() {
  return (
    <CheckboxField
      optionName="needSendEmailToManager"
      text="Enviar certificado por email para os gestores?"
      helpText="Opção para enviar o certificado para o email dos gestores, quando o aluno for LMS"  
    />
  );
}

export function SubmitActionsField() {
  const {submit, submitError, reset, options} = useCertificate();

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);


  const onCopyLinkPress = () => {
    navigator.clipboard.writeText(certificateUrl);
    setIsLinkCopied(true);
    setTimeout(() => {
      setIsLinkCopied(false); 
    }, 1000);
  }

  useEffect(() => {
    if(certificateUrl) {
      setCertificateUrl('');
    }
  }, [options]);

  const onSubmit = async () => {
    setIsLoading(true);
    
    const data = await submit();
    
    if(data) {
      setCertificateUrl(data?.url);
    }

    setIsLoading(false);
  }

  return (
    <>
      <SubmitActionsContainer>
        <RenderIf isTrue={!certificateUrl}>
          <Button loading={isLoading} loadingPlaceholder="Gerando ..." text={"Gerar"} onPress={onSubmit} />
        </RenderIf>
        <RenderIf isTrue={!!certificateUrl}>
          <Button
            LeadingIconElement={isLinkCopied ? <IoMdCheckmark /> : <IoMdClipboard />}
            text={isLinkCopied ? "Copiado" : "Copiar Link"}
            type="primary"
            mainColor={isLinkCopied ? '#32CD32' : '#F47C34'}
            minorColor="#FFF"
            onPress={onCopyLinkPress}
          />
          <HorizontaSeparator />
          <Button type="secondary" text="Abrir Link" onPress={() => window.open(certificateUrl)} TrailingIconElement={<IoMdOpen  />}/>
          <HorizontaSeparator />
          <Button type="tertiary" text="Limpar" onPress={reset} TrailingIconElement={<IoMdCloseCircleOutline />}/>
        </RenderIf>
      </SubmitActionsContainer>
      <RenderIf isTrue={!!submitError}>
        <ErrorContainer>
          <ErrorTitle>Houve um erro:</ErrorTitle>
          <ErrorText>{submitError}</ErrorText>
        </ErrorContainer>
      </RenderIf>
    </>
  )
}